import { BillItem, Billable } from '../pages/hospital/Billing/types';
import { BillItemKeys } from '../pages/hospital/Billing/types';

/**
 * Column configuration for validation
 */
export interface ValidationColumn {
  name: keyof BillItem;
  type: string;
  validation?: string;
}

/**
 * Validation error structure
 */
export interface ValidationError {
  field: string;
  errorKey: string;
  message?: string;
}

/**
 * Validates a bill item field value based on column configuration
 * @param value - The value to validate
 * @param itemKey - The field key being validated
 * @param column - Column configuration with validation rules
 * @param currentItem - Current bill item (for pharmacy validation)
 * @param billables - Available billables (for quantity checks)
 * @returns Error key string, empty if valid
 */
export function validateBillItemField(
  value: string,
  itemKey: string,
  column: ValidationColumn,
  currentItem?: BillItem,
  billables?: Billable[]
): string {
  let error = "";
  
  switch (column.type) {
    case "autocomplete":
      if (value.length < 3) {
        error = "concept_short";
      }
      break;
      
    case "amount":
    case "number":
      // Check for empty string first (empty string converts to 0 which may be misleading)
    //   if (value.trim() === '') {
    //     error = "valid_number";
    //     break;
    //   }
      
      const amount = Number(value);
      
      // Check for NaN
      if (isNaN(amount)) {
        error = "valid_number";
      } else if (amount < 0) {
        error = "amount_negative";
      } else if (column.validation === "pharmacyItem" && currentItem && billables) {
        const billableId = currentItem.billableId ?? currentItem.id;
        const pharmacyItem = billables.find((item) => item.id === billableId);
        if (pharmacyItem && amount > (pharmacyItem?.quantity as number)) {
          error = "amount_exceeded";
        } else if (amount === 0) {
          error = "amount_zero";
        }
      } else if (column.validation === "pharmacyRequest") {
        // SHER-470: a pharmacy request asks central pharmacy to replenish stock,
        // so the requested quantity is NOT capped by current on-hand stock
        // (unlike billing/dispensing, which keeps the `pharmacyItem` ceiling).
        // ~47% of the chom catalogue is out of stock — the very items a request
        // exists to reorder — so a stock ceiling here blocks the workflow. Only
        // require a positive amount.
        if (amount === 0) {
          error = "amount_zero";
        }
      }
      break;
  }
  
  return error;
}

/**
 * Validates all fields in a bill item
 * @param item - Bill item to validate
 * @param columns - Column configurations
 * @param billables - Available billables
 * @returns Array of validation errors
 */
export function validateBillItem(
  item: BillItem,
  columns: ValidationColumn[],
  billables?: Billable[]
): ValidationError[] {
  const errors: ValidationError[] = [];
  
  columns.forEach(column => {
    const value = String(item[column.name] || '');
    const errorKey = validateBillItemField(value, column.name, column, item, billables);
    
    if (errorKey) {
      errors.push({
        field: column.name,
        errorKey,
      });
    }
  });
  
  return errors;
}

/**
 * Validates quantity is positive integer
 * @param quantity - Quantity value to validate
 * @returns Error key or empty string
 */
export function validateQuantity(quantity: number): string {
  if (isNaN(quantity)) return "valid_number";
  if (quantity < 0) return "amount_negative";
  if (quantity === 0) return "amount_zero";
  if (!Number.isInteger(quantity)) return "must_be_integer";
  return "";
}

